// components/mobile/DetailSheet.jsx
// Bottom-sheet detail untuk satu item CardList: SEMUA kolom + tombol aksi.
// Full-width tombol (touch target >= 44px), tutup via backdrop / tombol X /
// tombol Back Android (history handled oleh halaman pemanggil bila perlu).
//
// Props:
//   open     : boolean
//   title    : node — header sheet (mis. username)
//   badge    : node | null — pill status di header
//   fields   : [{ label, value }] — value boleh node; null/'' → '—'
//   actions  : [{ key, label, Icon, onClick, disabled, tone, busy }]
//   onClose  : () => void
import React from 'react';
import { X, RefreshCw } from 'lucide-react';

const TONE = {
  amber:   'border-amber-300 bg-amber-50 text-amber-700 active:bg-amber-100',
  emerald: 'border-emerald-300 bg-emerald-50 text-emerald-700 active:bg-emerald-100',
  blue:    'border-blue-300 bg-blue-50 text-blue-700 active:bg-blue-100',
  rose:    'border-rose-300 bg-rose-50 text-rose-700 active:bg-rose-100',
  slate:   'border-slate-200 bg-white text-slate-600 active:bg-slate-50',
};

export default function DetailSheet({ open, title, badge, fields = [], actions = [], onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[900]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div
        className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-xl max-h-[85vh] overflow-y-auto"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
      >
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 pt-3 pb-2">
          <div className="mx-auto w-9 h-1 rounded-full bg-slate-200 mb-2" />
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="font-semibold text-slate-800 text-base truncate">{title}</h3>
              {badge}
            </div>
            <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-slate-400 active:text-slate-600">
              <X style={{ width: 20, height: 20 }} />
            </button>
          </div>
        </div>

        <dl className="px-4 py-3">
          {fields.map(({ label, value }) => (
            <div key={label} className="flex justify-between gap-4 py-1.5 border-b border-slate-50 last:border-0">
              <dt className="text-xs text-slate-400 shrink-0 pt-0.5">{label}</dt>
              <dd className="text-sm text-slate-700 text-right break-words min-w-0">
                {value === null || value === undefined || value === '' ? <span className="text-slate-300">—</span> : value}
              </dd>
            </div>
          ))}
        </dl>

        {actions.length > 0 && (
          <div className="px-4 pb-2 flex flex-col gap-2">
            {actions.map((a) => (
              <button key={a.key} onClick={a.onClick} disabled={a.disabled || a.busy}
                className={`w-full min-h-[44px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border
                  ${TONE[a.tone] || TONE.slate} disabled:opacity-40 disabled:cursor-not-allowed`}>
                {a.busy
                  ? <RefreshCw className="animate-spin" style={{ width: 15, height: 15 }} />
                  : a.Icon && <a.Icon style={{ width: 15, height: 15 }} />}
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
